/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { Box, IconButton, Typography, Divider } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import useTableSort from '../../hooks/useTableSort';
import SortableTableHeader from '../shared/SortableTableHeader';
import '../../styles/FacilityManagement.css';

const FacilityManagement = () => {
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingFacility, setEditingFacility] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [newFacility, setNewFacility] = useState({
    facility_name: '',
    facility_description: ''
  });

  // Filter facilities by search term
  const filteredFacilities = facilities.filter(facility => {
    return searchTerm === '' ||
      facility.facility_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      facility.facility_description?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Sorting hook
  const { sortedData, sortColumn, sortOrder, handleSort, resetSort } = useTableSort(
    filteredFacilities,
    { defaultSortColumn: 'facility_name', defaultSortOrder: 'asc' }
  );

  useEffect(() => {
    fetchFacilities();
  }, []);

  const fetchFacilities = async () => {
    try {
      const response = await axios.get('/api/facilities');
      setFacilities(response.data);
      setLoading(false);
    } catch (error) {
      setError('Failed to fetch facilities');
      setLoading(false);
    }
  };

  const handleEditClick = (facility) => {
    setEditingFacility({
      ...facility,
      accounts_receivable: facility.accounts_receivable || []
    });
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    try {
      const errors = validateFacility(editingFacility);
      if (errors.length > 0) {
        toast.error(errors.join('\n'));
        return;
      }

      // Prepare accounts_receivable - filter out completely empty accounts
      const accounts = (editingFacility.accounts_receivable || []).filter(acc => 
        acc.account_number && acc.account_number.trim() !== ''
      );

      const facilityToSave = {
        facility_name: editingFacility.facility_name,
        facility_description: editingFacility.facility_description,
        accounts_receivable: accounts
      };

      const response = await axios.put(`/api/facilities/${editingFacility._id}`, facilityToSave);
      setEditDialogOpen(false);
      setEditingFacility(null);
      fetchFacilities();
      toast.success('Facility updated successfully');
    } catch (error) {
      console.error('Failed to update facility:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to update facility';
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this facility?')) {
      try {
        await axios.delete(`/api/facilities/${id}`);
        fetchFacilities();
      } catch (error) {
        console.error('Failed to delete facility:', error);
      }
    }
  };

  const validateFacility = (facility) => {
    const errors = [];
    
    if (!facility.facility_name?.trim()) {
      errors.push('Facility name is required');
    }

    if (!facility.facility_description?.trim()) {
      errors.push('Facility description is required');
    }

    return errors;
  };

  const handleCreate = async () => {
    try {
      const errors = validateFacility(newFacility);
      if (errors.length > 0) {
        toast.error(errors.join('\n'));
        return;
      }

      await axios.post('/api/facilities', newFacility);
      setNewFacility({
        facility_name: '',
        facility_description: ''
      });
      fetchFacilities();
      toast.success('Facility created successfully');
    } catch (error) {
      console.error('Failed to create facility:', error);
      toast.error(error.response?.data?.error || 'Failed to create facility');
    }
  };

  const handleAddAccount = () => {
    setEditingFacility({
      ...editingFacility,
      accounts_receivable: [
        ...(editingFacility.accounts_receivable || []),
        { account_number: '', description: '' }
      ]
    });
  };

  const handleRemoveAccount = (index) => {
    // Allow deletion of any account - invoices can be remapped later
    const updatedAccounts = [...(editingFacility.accounts_receivable || [])];
    updatedAccounts.splice(index, 1);
    setEditingFacility({
      ...editingFacility,
      accounts_receivable: updatedAccounts
    });
  };

  const handleAccountChange = (index, field, value) => {
    const updatedAccounts = [...(editingFacility.accounts_receivable || [])];
    updatedAccounts[index] = {
      ...updatedAccounts[index],
      [field]: value
    };
    setEditingFacility({
      ...editingFacility,
      accounts_receivable: updatedAccounts
    });
  };

  const renderEditForm = () => (
    <Dialog 
      open={editDialogOpen} 
      onClose={() => setEditDialogOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Edit Facility</DialogTitle>
      <DialogContent>
        <div className="edit-form">
          <TextField
            label="Facility Name"
            fullWidth
            margin="normal"
            value={editingFacility?.facility_name || ''}
            onChange={(e) => setEditingFacility({
              ...editingFacility,
              facility_name: e.target.value
            })}
          />
          <TextField
            label="Facility Description"
            fullWidth
            margin="normal"
            multiline
            rows={4}
            value={editingFacility?.facility_description || ''}
            onChange={(e) => setEditingFacility({
              ...editingFacility,
              facility_description: e.target.value
            })}
          />
          
          <Divider sx={{ my: 3 }} />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Accounts Receivable</Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddAccount}
            >
              Add Account
            </Button>
          </Box>
          
          {(editingFacility?.accounts_receivable || []).map((account, index) => (
            <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <IconButton aria-label="Delete"
                  size="small"
                  color="error"
                  onClick={() => handleRemoveAccount(index)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
              <TextField
                label="Account Number"
                fullWidth
                margin="normal"
                size="small"
                value={account.account_number || ''}
                onChange={(e) => handleAccountChange(index, 'account_number', e.target.value)}
              />
              <TextField
                label="Description"
                fullWidth
                margin="normal"
                size="small"
                value={account.description || ''}
                onChange={(e) => handleAccountChange(index, 'description', e.target.value)}
              />
            </Box>
          ))}
          
          {(!editingFacility?.accounts_receivable || editingFacility.accounts_receivable.length === 0) && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No accounts receivable added yet. Click "Add Account" to add one.
            </Typography>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
        <Button onClick={handleEditSave} variant="contained" color="primary">
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderCreateForm = () => (
    <Dialog 
      open={createDialogOpen} 
      onClose={() => setCreateDialogOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Create New Facility</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          margin="normal"
          label="Facility Name"
          value={newFacility.facility_name}
          onChange={(e) => setNewFacility({
            ...newFacility,
            facility_name: e.target.value
          })}
          required
        />
        <TextField
          fullWidth
          margin="normal"
          label="Facility Description"
          multiline
          rows={4}
          value={newFacility.facility_description}
          onChange={(e) => setNewFacility({
            ...newFacility,
            facility_description: e.target.value
          })}
          required
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
        <Button onClick={handleCreate} variant="contained" color="primary">
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="facility-management">
      <h2>Facility Management</h2>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
        <TextField
          label="Search Facilities"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name, description..."
          sx={{ minWidth: 300 }}
        />
      </Box>

      <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setCreateDialogOpen(true)}
        >
          Add New Facility
        </Button>
        <Button
          variant="outlined"
          onClick={resetSort}
        >
          Reset Sort
        </Button>
      </Box>

      <table className="data-table">
        <thead>
          <tr>
            <SortableTableHeader 
              column="facility_name" 
              label="Facility Name" 
              currentSortColumn={sortColumn}
              sortOrder={sortOrder}
              onSort={handleSort}
            />
            <SortableTableHeader 
              column="facility_description" 
              label="Facility Description" 
              currentSortColumn={sortColumn}
              sortOrder={sortOrder}
              onSort={handleSort}
            />
            <th>Accounts Receivable</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map(facility => (
            <tr key={facility._id}>
              <td>{facility.facility_name}</td>
              <td>{facility.facility_description}</td>
              <td>{(facility.accounts_receivable || []).length} account(s)</td>
              <td className="action-buttons">
                <Button
                  variant="contained"
                  size="small"
                  className="row-action-edit"
                  style={{ marginRight: '8px' }}
                  onClick={() => handleEditClick(facility)}
                >
                  Edit
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="error"
                  onClick={() => handleDelete(facility._id)}
                >
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {renderEditForm()}
      {renderCreateForm()}
    </div>
  );
};

export default FacilityManagement;